import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { SyncedMenuItem, ModifierGroupData } from '@/types/api';
import { ColorPickerField } from '@/components/ui/ColorPickerField';
import { TranslationsEditor } from '@/components/ui/TranslationsEditor';
import { MenuItemMenuGroupDialog } from './MenuItemMenuGroupDialog';
import { useEntityData } from '@/hooks/useEntityData';

// `available` isn't on SyncedMenuItem (that type's visibility flag is
// `visible`, matching the GET response) -- but the update/create payload
// this form actually writes to uses `available` (see UpdateMenuItemPayload),
// so the form's own state needs to carry it too (visible-vs-available fix).
type MenuItemFormData = Partial<SyncedMenuItem> & { available?: boolean };

interface MenuItemFormProps {
  formData: MenuItemFormData;
  onChange: (data: MenuItemFormData) => void;
  restaurantId: number;
}

interface SelectOption {
  id: number | string;
  posAccountSalesAreaId?: string;
  name: string;
  [key: string]: unknown;
}

const DIETARY_OPTIONS = [
  { key: 'Vegan', label: 'Vegan' },
  { key: 'Vegetarian', label: 'Vegetarian' },
  { key: 'Halal', label: 'Halal' },
  { key: 'Gluten_Free', label: 'Gluten Free' },
  { key: 'Non_Veg', label: 'Non-Veg' },
];

const cbClass = "h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500";
const mcbClass = "h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500";

function NumInput({ label, value, onChange, step, min, max }: { label: string; value?: number | null; onChange: (v?: number) => void; step?: string; min?: string; max?: string }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type="number" step={step ?? '1'} min={min} max={max} value={value ?? ''} onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : undefined)} className="input w-full" />
    </div>
  );
}

function Check({ id, label, checked, onChange }: { id: string; label: string; checked?: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center">
      <input type="checkbox" id={id} checked={checked ?? false} onChange={(e) => onChange(e.target.checked)} className={cbClass} />
      <label htmlFor={id} className="ml-2 text-sm text-gray-700">{label}</label>
    </div>
  );
}

function MultiCheckList({ label, items, selectedIds, onToggle, idKey, labelKey, isLoading, isError, onRetry }: {
  label: string;
  items: SelectOption[];
  selectedIds: number[];
  onToggle: (id: number) => void;
  idKey?: string;
  labelKey?: string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
}) {
  const getLabel = (item: SelectOption) => String((labelKey ? item[labelKey] : item.name) || item.name);
  // Returns null when the entity has no id in the expected space yet -- a mgmt-created
  // entity has a null posXxxId until the POS acks it. Coercing that with Number()
  // yields NaN (or 0 for a literal null), which silently corrupts the JSONB reference
  // array on toggle (F2).
  const getId = (item: SelectOption): number | null => {
    const raw = idKey ? item[idKey] : item.id;
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  // Previously `if (items.length === 0) return null` here, which made a genuine
  // "no options for this restaurant" state, a still-loading query, and a failed
  // query all collapse into the control silently not existing -- the user had no
  // way to tell a failed fetch from "nothing to configure" (F12). Now the control
  // itself is always rendered, and only its body switches on the query state.
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {isLoading ? (
        <div className="border rounded-md p-2 text-sm text-gray-400 italic">Loading options…</div>
      ) : isError ? (
        <div className="border border-red-300 rounded-md p-2 text-sm text-red-600 flex items-center justify-between gap-2" role="alert">
          <span>Unable to load options.</span>
          {onRetry && (
            <button type="button" onClick={onRetry} className="text-red-700 underline hover:text-red-800 shrink-0">
              Retry
            </button>
          )}
        </div>
      ) : items.length === 0 ? (
        <div className="border rounded-md p-2 text-sm text-gray-400 italic">No options configured</div>
      ) : (
        <div className="border rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
          {items.map((item) => {
            const posId = getId(item);
            const unsynced = posId === null;
            const checked = posId !== null && selectedIds.includes(posId);
            return (
              <div key={item.id} className="flex items-center">
                <input
                  type="checkbox"
                  id={`mi-${label}-${item.id}`}
                  checked={checked}
                  disabled={unsynced}
                  title={unsynced ? 'Not yet synced to the POS -- cannot be linked yet' : undefined}
                  onChange={() => posId !== null && onToggle(posId)}
                  className={mcbClass}
                />
                <label
                  htmlFor={`mi-${label}-${item.id}`}
                  className={`ml-2 text-sm ${unsynced ? 'text-gray-400 italic' : 'text-gray-700'}`}
                >
                  {getLabel(item)}{unsynced ? ' (unsynced)' : ''}
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function MenuItemForm({ formData, onChange, restaurantId }: MenuItemFormProps) {
  const set = (patch: MenuItemFormData) => onChange({ ...formData, ...patch });

  const [mgDialogOpen, setMgDialogOpen] = useState(false);
  const [mgEditData, setMgEditData] = useState<ModifierGroupData | undefined>(undefined);
  const [mgSelectedIdx, setMgSelectedIdx] = useState<number | null>(null);

  // Dropdown data shares the same cached/deduped/invalidation-aware queries as the
  // main entity table, instead of a raw Promise.all fetched fresh on every mount.
  const { data: menuGroups = [], isLoading: loadingMenuGroups } = useEntityData('menuGroups', restaurantId);
  const { data: printerGroups = [], isLoading: loadingPrinterGroups, isError: errorPrinterGroups, refetch: refetchPrinterGroups } = useEntityData('printerGroups', restaurantId);
  const { data: taxes = [], isLoading: loadingTaxes } = useEntityData('taxes', restaurantId);
  const { data: orderTypes = [], isLoading: loadingOrderTypes, isError: errorOrderTypes, refetch: refetchOrderTypes } = useEntityData('orderTypes', restaurantId);
  const { data: cookingInstructions = [], isLoading: loadingCookingInstructions, isError: errorCookingInstructions, refetch: refetchCookingInstructions } = useEntityData('cookingInstructions', restaurantId);
  const { data: rawShifts = [], isLoading: loadingShifts, isError: errorShifts, refetch: refetchShifts } = useEntityData('shifts', restaurantId);
  const { data: accountSalesAreas = [], isLoading: loadingAccountSalesAreas } = useEntityData('accountSalesAreas', restaurantId);
  const { data: rawTables = [], isLoading: loadingTables, isError: errorTables, refetch: refetchTables } = useQuery({
    queryKey: ['managed-tables', restaurantId],
    queryFn: () => apiClient.getManagedTables(restaurantId),
    enabled: !!restaurantId,
  });

  const shifts = useMemo<SelectOption[]>(
    () => (rawShifts as any[]).map((s: any) => ({ id: s.id, posShiftId: s.posShiftId, name: s.name })),
    [rawShifts]
  );
  const tables = useMemo<SelectOption[]>(
    () => (rawTables as any[]).map((t: any) => ({ id: t.id, posTableId: t.posTableId, name: t.name })),
    [rawTables]
  );

  const loading = loadingMenuGroups || loadingPrinterGroups || loadingTaxes || loadingOrderTypes
    || loadingCookingInstructions || loadingShifts || loadingAccountSalesAreas || loadingTables;

  const handleArrayToggle = (field: keyof SyncedMenuItem, id: number) => {
    const current = ((formData as any)[field] || []) as number[];
    const updated = current.includes(id)
      ? current.filter((x: number) => x !== id)
      : [...current, id];
    set({ [field]: updated } as any);
  };

  const handleDietaryToggle = (key: string) => {
    const current = formData.dietaryFlags || {};
    const newVal = !current[key];
    const updated = { ...current };
    if (newVal) {
      updated[key] = true;
    } else {
      delete updated[key];
    }
    set({ dietaryFlags: updated });
  };

  return (
    <div className="space-y-5">
      {/* Basic Info */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="text-sm font-semibold text-gray-600 px-2">Basic Info</legend>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input type="text" value={formData.name || ''} onChange={(e) => set({ name: e.target.value })} className="input w-full" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={formData.description || ''} onChange={(e) => set({ description: e.target.value })} className="input w-full" rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
              <input type="number" step="0.01" min="0" value={formData.price ?? ''} onChange={(e) => set({ price: parseFloat(e.target.value) || 0 })} className="input w-full" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Menu Group *</label>
              <select
                value={formData.groupId ?? ''}
                onChange={(e) => set({ groupId: e.target.value ? parseInt(e.target.value) : undefined })}
                className="input w-full"
                disabled={loading}
                required
              >
                <option value="">-- Select Menu Group --</option>
                {menuGroups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* Writes `available`, not `visible` -- the update/create payload only
                reads `available` (UpdateMenuItemRequest), so binding this to `visible`
                made the checkbox a silent no-op on save (visible-vs-available fix). */}
            <Check id="mi-visible" label="Visible" checked={formData.available ?? true} onChange={(v) => set({ available: v })} />
            <NumInput label="Sort Order" value={formData.sortOrder} onChange={(v) => set({ sortOrder: v ?? 0 })} />
          </div>
        </div>
      </fieldset>

      {/* Tax & Printer */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="text-sm font-semibold text-gray-600 px-2">Tax & Printer</legend>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tax</label>
            <select
              value={formData.taxId ?? ''}
              onChange={(e) => set({ taxId: e.target.value ? parseInt(e.target.value) : undefined })}
              className="input w-full"
              disabled={loading}
            >
              <option value="">-- None --</option>
              {taxes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}{(t as any).rate != null ? ` (${(t as any).rate}%)` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Printer Group</label>
            <select
              value={formData.printerGroupId ?? ''}
              onChange={(e) => set({ printerGroupId: e.target.value ? parseInt(e.target.value) : undefined })}
              className="input w-full"
              disabled={loading}
            >
              {/* formData.printerGroupId stores ManagedPrinterGroup.id (see
                  OutboundSyncServiceImpl's managedPrinterGroupRepository.findById
                  lookup) -- not posPrinterGroupId, which is a different id space
                  entirely and was never the value being stored/compared here. */}
              <option value="">-- None --</option>
              {printerGroups.map((pg: any) => (
                <option key={pg.id} value={pg.id}>{pg.name}</option>
              ))}
            </select>
          </div>
        </div>
      </fieldset>

      {/* Pricing */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="text-sm font-semibold text-gray-600 px-2">Pricing</legend>
        <div className="grid grid-cols-2 gap-3">
          <NumInput label="Buy Price" value={formData.buyPrice} onChange={(v) => set({ buyPrice: v })} step="0.01" min="0" />
          <NumInput label="Web Price" value={formData.webPrice} onChange={(v) => set({ webPrice: v })} step="0.01" min="0" />
          <NumInput label="Discount Rate (%)" value={formData.discountRate} onChange={(v) => set({ discountRate: v })} step="0.01" min="0" max="100" />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit Name</label>
            <input type="text" value={formData.unitName || ''} onChange={(e) => set({ unitName: e.target.value })} className="input w-full" placeholder="e.g. kg, lb, each" />
          </div>
        </div>
      </fieldset>

      {/* Inventory */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="text-sm font-semibold text-gray-600 px-2">Inventory</legend>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
            <input type="text" value={formData.barcode || ''} onChange={(e) => set({ barcode: e.target.value })} className="input w-full" />
          </div>
          <NumInput label="Stock Amount" value={formData.stockAmount} onChange={(v) => set({ stockAmount: v })} step="0.01" min="0" />
          <Check id="mi-manageStock" label="Manage Stock" checked={formData.manageStock} onChange={(v) => set({ manageStock: v })} />
          <Check id="mi-disableWhenStockAmountIsZero" label="Disable When Stock is Zero" checked={formData.disableWhenStockAmountIsZero} onChange={(v) => set({ disableWhenStockAmountIsZero: v })} />
        </div>
      </fieldset>

      {/* Display */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="text-sm font-semibold text-gray-600 px-2">Display</legend>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <ColorPickerField label="Button Color" value={formData.buttonColorCode} onChange={(v) => set({ buttonColorCode: v })} />
          <ColorPickerField label="Text Color" value={formData.textColorCode} onChange={(v) => set({ textColorCode: v })} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Check id="mi-appendToParentName" label="Append to Parent Name" checked={formData.appendToParentName} onChange={(v) => set({ appendToParentName: v })} />
          <Check id="mi-prependToParentName" label="Prepend to Parent Name" checked={formData.prependToParentName} onChange={(v) => set({ prependToParentName: v })} />
          <Check id="mi-prependAsterisk" label="Prepend Asterisk" checked={formData.prependAsterisk} onChange={(v) => set({ prependAsterisk: v })} />
          <Check id="mi-freeDisplayForChild" label="Free Display for Child" checked={formData.freeDisplayForChild} onChange={(v) => set({ freeDisplayForChild: v })} />
          <Check id="mi-showStatusButton" label="Show Status Button" checked={formData.showStatusButton} onChange={(v) => set({ showStatusButton: v })} />
        </div>
      </fieldset>

      {/* Quantity */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="text-sm font-semibold text-gray-600 px-2">Quantity</legend>
        <div className="grid grid-cols-2 gap-3">
          <NumInput label="Min Quantity Group" value={formData.minQuantityGroup} onChange={(v) => set({ minQuantityGroup: v })} min="0" />
          <NumInput label="Max Quantity Group" value={formData.maxQuantityGroup} onChange={(v) => set({ maxQuantityGroup: v })} min="0" />
        </div>
      </fieldset>

      {/* Kitchen & Receipt */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="text-sm font-semibold text-gray-600 px-2">Kitchen & Receipt</legend>
        <div className="grid grid-cols-2 gap-2">
          <Check id="mi-hideOnKitchen" label="Hide on Kitchen" checked={formData.hideOnKitchen} onChange={(v) => set({ hideOnKitchen: v })} />
          <Check id="mi-hideOnReceipt" label="Hide on Receipt" checked={formData.hideOnReceipt} onChange={(v) => set({ hideOnReceipt: v })} />
          <Check id="mi-enforceInstructions" label="Enforce Instructions" checked={formData.enforceInstructions} onChange={(v) => set({ enforceInstructions: v })} />
        </div>
      </fieldset>

      {/* Online */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="text-sm font-semibold text-gray-600 px-2">Online</legend>
        <div className="grid grid-cols-2 gap-2">
          <Check id="mi-visibleOnSite" label="Visible on Site" checked={formData.visibleOnSite} onChange={(v) => set({ visibleOnSite: v })} />
          <Check id="mi-disableCookingInfoOnline" label="Disable Cooking Info Online" checked={formData.disableCookingInfoOnline} onChange={(v) => set({ disableCookingInfoOnline: v })} />
        </div>
      </fieldset>

      {/* Behavior */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="text-sm font-semibold text-gray-600 px-2">Behavior</legend>
        <div className="grid grid-cols-2 gap-2">
          <Check id="mi-askMealDeal" label="Ask Meal Deal" checked={formData.askMealDeal} onChange={(v) => set({ askMealDeal: v })} />
          <Check id="mi-askForPrice" label="Ask for Price" checked={formData.askForPrice} onChange={(v) => set({ askForPrice: v })} />
          <Check id="mi-giveRestaurantPoints" label="Give Restaurant Points" checked={formData.giveRestaurantPoints} onChange={(v) => set({ giveRestaurantPoints: v })} />
          <Check id="mi-alcoholicDrink" label="Alcoholic Drink" checked={formData.alcoholicDrink} onChange={(v) => set({ alcoholicDrink: v })} />
          <Check id="mi-hasDouble" label="Has Double" checked={formData.hasDouble} onChange={(v) => set({ hasDouble: v })} />
          <Check id="mi-openItem" label="Open Item" checked={formData.openItem} onChange={(v) => set({ openItem: v })} />
        </div>
      </fieldset>

      {/* Associations */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="text-sm font-semibold text-gray-600 px-2">Associations</legend>
        <div className="grid grid-cols-2 gap-4">
          <MultiCheckList
            label="Order Types"
            items={orderTypes}
            selectedIds={formData.orderTypeIds || []}
            onToggle={(id) => handleArrayToggle('orderTypeIds', id)}
            idKey="posOrderTypeId"
            isLoading={loadingOrderTypes}
            isError={errorOrderTypes}
            onRetry={() => refetchOrderTypes()}
          />
          <MultiCheckList
            label="Cooking Instructions"
            items={cookingInstructions}
            selectedIds={formData.cookingInstructionIds || []}
            onToggle={(id) => handleArrayToggle('cookingInstructionIds', id)}
            idKey="posInstructionId"
            labelKey="description"
            isLoading={loadingCookingInstructions}
            isError={errorCookingInstructions}
            onRetry={() => refetchCookingInstructions()}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dietary</label>
            <div className="border rounded-md p-2 space-y-1">
              {DIETARY_OPTIONS.map((d) => {
                const checked = !!(formData.dietaryFlags || {})[d.key];
                return (
                  <div key={d.key} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`mi-dietary-${d.key}`}
                      checked={checked}
                      onChange={() => handleDietaryToggle(d.key)}
                      className={mcbClass}
                    />
                    <label htmlFor={`mi-dietary-${d.key}`} className="ml-2 text-sm text-gray-700">{d.label}</label>
                  </div>
                );
              })}
            </div>
          </div>
          <MultiCheckList
            label="Additional Printer Groups"
            items={printerGroups}
            selectedIds={formData.additionalPrinterGroupIds || []}
            onToggle={(id) => handleArrayToggle('additionalPrinterGroupIds', id)}
            idKey="posPrinterGroupId"
            isLoading={loadingPrinterGroups}
            isError={errorPrinterGroups}
            onRetry={() => refetchPrinterGroups()}
          />
          <MultiCheckList
            label="Shifts"
            items={shifts}
            selectedIds={formData.shiftIds || []}
            onToggle={(id) => handleArrayToggle('shiftIds', id)}
            idKey="posShiftId"
            isLoading={loadingShifts}
            isError={errorShifts}
            onRetry={() => refetchShifts()}
          />
          <MultiCheckList
            label="Banned Tables"
            items={tables}
            selectedIds={formData.bannedTableIds || []}
            onToggle={(id) => handleArrayToggle('bannedTableIds', id)}
            idKey="posTableId"
            isLoading={loadingTables}
            isError={errorTables}
            onRetry={() => refetchTables()}
          />
        </div>
      </fieldset>

      {/* Modifier Groups */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="text-sm font-semibold text-gray-600 px-2">Modifier Groups</legend>
        <div className="space-y-3">
          <div className="flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => { setMgEditData(undefined); setMgDialogOpen(true); }}
            >
              Add
            </button>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              disabled={mgSelectedIdx === null}
              onClick={() => {
                if (mgSelectedIdx !== null) {
                  setMgEditData((formData.modifierGroups || [])[mgSelectedIdx]);
                  setMgDialogOpen(true);
                }
              }}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              disabled={mgSelectedIdx === null}
              onClick={() => {
                if (mgSelectedIdx !== null) {
                  const updated = [...(formData.modifierGroups || [])];
                  updated.splice(mgSelectedIdx, 1);
                  set({ modifierGroups: updated });
                  setMgSelectedIdx(null);
                }
              }}
            >
              Delete
            </button>
          </div>
          <div className="overflow-auto max-h-48 border rounded">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left">Menu Group</th>
                  <th className="px-2 py-1 text-center">Min</th>
                  <th className="px-2 py-1 text-center">Max</th>
                  <th className="px-2 py-1 text-center">Order</th>
                  <th className="px-2 py-1 text-center">Extra</th>
                </tr>
              </thead>
              <tbody>
                {(formData.modifierGroups || []).length === 0 && (
                  <tr><td colSpan={5} className="px-2 py-3 text-center text-gray-400">No modifier groups</td></tr>
                )}
                {(formData.modifierGroups || []).map((mg, idx) => (
                  <tr
                    key={mg.uuid || idx}
                    className={`cursor-pointer ${mgSelectedIdx === idx ? 'bg-blue-100 ring-2 ring-blue-400' : 'hover:bg-gray-100'}`}
                    onClick={() => setMgSelectedIdx(mgSelectedIdx === idx ? null : idx)}
                    onDoubleClick={() => { setMgSelectedIdx(idx); setMgEditData(mg); setMgDialogOpen(true); }}
                  >
                    <td className="px-2 py-1">{mg.menuGroupName || `Group #${mg.menuGroupId}`}</td>
                    <td className="px-2 py-1 text-center">{mg.minQuantity ?? '-'}</td>
                    <td className="px-2 py-1 text-center">{mg.maxQuantity ?? '-'}</td>
                    <td className="px-2 py-1 text-center">{mg.sortOrder ?? '-'}</td>
                    <td className="px-2 py-1 text-center">{mg.extra ? 'Yes' : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </fieldset>

      {/* menuGroupId stores a POS-native id (see MenuManagementServiceImpl comment) --
          no `?? g.id` fallback, and unsynced groups (posGroupId == null) are dropped
          here rather than shown, since this dialog's <select> has no disabled-option
          affordance of its own (F3). */}
      <MenuItemMenuGroupDialog
        open={mgDialogOpen}
        onClose={() => { setMgDialogOpen(false); setMgEditData(undefined); }}
        onSave={(data) => {
          const current = formData.modifierGroups || [];
          const existingIdx = current.findIndex((m) => m.uuid === data.uuid);
          if (existingIdx >= 0) {
            const updated = [...current];
            updated[existingIdx] = data;
            set({ modifierGroups: updated });
          } else {
            set({ modifierGroups: [...current, data] });
          }
          setMgSelectedIdx(null);
        }}
        initialData={mgEditData}
        menuGroups={menuGroups
          .filter((g: any) => g.posGroupId != null)
          .map((g: any) => ({ id: g.posGroupId, name: g.name }))}
        existingMenuGroupIds={(formData.modifierGroups || []).map((m) => m.menuGroupId!).filter(Boolean)}
      />

      {/* Classification */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="text-sm font-semibold text-gray-600 px-2">Classification</legend>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Account Sales Area</label>
          <select
            value={formData.saleAreaId || ''}
            onChange={(e) => set({ saleAreaId: e.target.value || undefined })}
            className="input w-full"
            disabled={loading}
          >
            <option value="">-- None --</option>
            {accountSalesAreas.map((asa) => {
              // saleAreaId is a String (POS uses GlobalIdGenerator, not a numeric
              // id, for this entity) -- no `asa.id` fallback at all, since even a
              // stringified mgmt PK would still be the wrong id space (F3).
              const posId = asa.posAccountSalesAreaId;
              return (
                <option key={asa.id} value={posId ?? ''} disabled={posId == null}>
                  {asa.name}{posId == null ? ' (unsynced)' : ''}
                </option>
              );
            })}
          </select>
        </div>
      </fieldset>

      {/* Translations */}
      <TranslationsEditor
        restaurantId={restaurantId}
        translations={formData.translations || []}
        onChange={(translations) => set({ translations })}
      />
    </div>
  );
}
